import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Which columns a list shows, and in what order — remembered per list.
 *
 * The preference is stored as a list of keys plus a hidden set, NOT as a copy
 * of the column definitions. Storing the definitions would freeze whatever the
 * app looked like on the day the user first opened the page: a column added in
 * a later release would never appear for anyone who had ever touched the
 * picker, and a column that was removed would linger as a blank.
 *
 * Reconciling against the CURRENT definitions on every load means:
 *   - a new column appears, in its natural position, already visible
 *   - a removed column disappears, even if it is still in someone's storage
 *   - the user's ordering and hiding survive both
 *
 * Storage is per browser. It is a display preference, so losing it (private
 * window, cleared site data) costs nothing but the default layout — which is
 * why a failed read is swallowed rather than surfaced.
 */
export function useColumnPrefs(storageKey, allColumns) {
  const defaultOrder = useMemo(() => allColumns.map((c) => c.key), [allColumns]);

  const [order, setOrder] = useState(defaultOrder);
  const [hidden, setHidden] = useState(() => new Set());
  const [loaded, setLoaded] = useState(false);

  // Read once, then reconcile against the columns this build actually has.
  useEffect(() => {
    let savedOrder = null;
    let savedHidden = [];
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.order)) savedOrder = parsed.order;
        if (Array.isArray(parsed.hidden)) savedHidden = parsed.hidden;
      }
    } catch {
      // Unreadable or unavailable storage: fall back to the defaults.
    }

    const known = new Set(defaultOrder);
    // Columns marked defaultHidden start off, so a list can offer many fields
    // without shipping an unreadably wide table to someone who never opened
    // the picker. Only applies on a FIRST visit: once a preference exists the
    // user's choice wins, including choosing to hide something we show.
    const firstVisit = savedOrder === null;
    // Keep the saved order for columns that still exist...
    const kept = (savedOrder || []).filter((k) => known.has(k));
    // ...then append anything the saved order never knew about, in its
    // natural position relative to the columns around it.
    const missing = defaultOrder.filter((k) => !kept.includes(k));
    setOrder([...kept, ...missing]);
    if (firstVisit) {
      setHidden(new Set(allColumns.filter((c) => c.defaultHidden).map((c) => c.key)));
    } else {
      setHidden(new Set(savedHidden.filter((k) => known.has(k))));
    }
    setLoaded(true);
  }, [storageKey, defaultOrder, allColumns]);

  // Persist only after the first read, or the initial render would overwrite
  // the stored preference with the defaults before it had been applied.
  useEffect(() => {
    if (!loaded) return;
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ order, hidden: Array.from(hidden) }),
      );
    } catch {
      // Storage full or blocked: the layout still works for this session.
    }
  }, [storageKey, order, hidden, loaded]);

  const byKey = useMemo(
    () => Object.fromEntries(allColumns.map((c) => [c.key, c])),
    [allColumns],
  );

  /** Every column in the user's order, each tagged with its visibility. */
  const ordered = useMemo(
    () => order.map((k) => byKey[k]).filter(Boolean).map((c) => ({
      ...c,
      visible: !hidden.has(c.key),
    })),
    [order, byKey, hidden],
  );

  /** Just the ones to render, in order. */
  const visibleColumns = useMemo(
    () => ordered.filter((c) => c.visible),
    [ordered],
  );

  const toggle = useCallback((key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Never let the last column be hidden — an empty table with no way
        // back is a dead end the user cannot escape without clearing storage.
        const visibleCount = order.filter((k) => !prev.has(k)).length;
        if (visibleCount <= 1) return prev;
        next.add(key);
      }
      return next;
    });
  }, [order]);

  const move = useCallback((key, delta) => {
    setOrder((prev) => {
      const i = prev.indexOf(key);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setOrder(defaultOrder);
    setHidden(new Set(allColumns.filter((c) => c.defaultHidden).map((c) => c.key)));
  }, [defaultOrder, allColumns]);

  return { ordered, visibleColumns, toggle, move, reset };
}

export default useColumnPrefs;
